import { createClient, type Session } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!url || !anonKey) {
  throw new Error(
    "VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are missing. Run `make web-env`.",
  );
}

/**
 * Auth only. The app never queries Postgres through this client — every read and write
 * goes through the API (D-4). Supabase is used here for the magic link and, later, for
 * signed-URL uploads to Storage.
 */
export const supabase = createClient(url, anonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
});

export type { Session };

export async function sendMagicLink(email: string): Promise<void> {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      // An explicit callback route, not the bare origin.
      //
      // `window.location.origin` looked right and was the source of a real production bug: with the
      // hosted project's `uri_allow_list` empty, Supabase ignores an unlisted redirect and falls back
      // to `site_url` — which was still the default `http://localhost:3000`, so every magic link sent
      // from prod pointed at a machine that was not there. The origin is kept (so local dev lands on
      // localhost:5173 and prod on crm.cstlegacy.com) but the path is now a route that exists and
      // knows how to finish the job, and both origins are on their respective allow-lists.
      emailRedirectTo: `${window.location.origin}/auth/callback`,
      // Access is an allow-list (D-4): never create an account from a login attempt.
      shouldCreateUser: false,
    },
  });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}
